import { AssetHistoryType } from 'further-types/lp/asset';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { sortBy } from 'lodash';

export type ProcessedAssetHistory = {
  id: string;
  holdingClassName: string;
  holdingClassId: string;
  unrealisedValue: number;
  unitPrice: number;
  sharesHeld: number;
  cumulativeSharesSold: number;
  cumulativeRealisedValue: number;
  valuationChange: number | undefined;
  valuationChangePercentage: number | undefined;
  date: Date;
  currency: CurrencyCode;
  type: AssetHistoryType;
};

type AssetHistoryEntry = {
  type: AssetHistoryType;
  holdingClassId: string | undefined;
  holdingClassName: string;
  ledgerDate: Date;
  unitPriceTcy: number;
  unitPriceLcy: number;
  currentNumberOfUnits: number;
  currency: CurrencyCode;
  assetId: string;
  unitsSold?: number;
  realisationAmountTcy?: number;
  realisationAmountLcy?: number;
};

function processAssetFullHistory(
  assetFullHistory: Record<string, Array<AssetHistoryEntry>>,
): Array<ProcessedAssetHistory> {
  const processedData: Array<ProcessedAssetHistory> = [];

  Object.entries(assetFullHistory).forEach(([holdingClassId, history]) => {
    if (holdingClassId === 'unknown' || !holdingClassId) return;

    const sortedHistory = sortBy(history, 'ledgerDate');
    let cumulativeSharesSold = 0;
    let cumulativeRealisedValue = 0;

    sortedHistory.forEach((entry, index) => {
      const unitPrice = entry.unitPriceTcy;
      const sharesHeld = entry.currentNumberOfUnits;
      const unrealisedValue = unitPrice * sharesHeld;

      // Update cumulative values if this is a realisation
      if (entry.type === AssetHistoryType.Realisation) {
        cumulativeSharesSold += entry.unitsSold ?? 0;
        cumulativeRealisedValue += entry.realisationAmountTcy ?? 0;
      }

      // Calculate valuation change compared to previous entry (excluding first investment and realisations)
      let valuationChange: number | undefined;
      let valuationChangePercentage: number | undefined;

      if (
        index > 0 &&
        entry.type !== AssetHistoryType.Investment &&
        entry.type !== AssetHistoryType.Realisation
      ) {
        const previousEntry = sortedHistory[index - 1];
        const previousUnitPrice = previousEntry.unitPriceTcy;
        const previousValuation = previousUnitPrice * sharesHeld;

        valuationChange = unrealisedValue - previousValuation;
        valuationChangePercentage =
          previousValuation !== 0 ? valuationChange / previousValuation : 0;
      }

      processedData.push({
        id: `${entry.assetId}-${holdingClassId}-${
          entry.type
        }-${entry.ledgerDate.toISOString()}`,
        holdingClassName: entry.holdingClassName,
        holdingClassId,
        unrealisedValue,
        unitPrice,
        sharesHeld,
        cumulativeSharesSold,
        cumulativeRealisedValue,
        valuationChange,
        valuationChangePercentage,
        date: entry.ledgerDate,
        currency: entry.currency,
        type: entry.type,
      });
    });
  });

  return sortBy(processedData, (row) => row.date);
}

export default processAssetFullHistory;
